import type { FormikContextType } from 'formik';

import type { ProjectOrQuoteProductFormikType } from '@core/formik/projectOrQuoteProduct';
import type { SharedFormikType } from '@core/hooks/useProductSection';
import type { TruckingTypeSummary } from '@core/types/api/TruckingTypeSummary/TruckingTypeSummary';
import type { LookupInputOption } from '@core/types/inputOption';
import type { List } from '@core/types/list';
import { cleanedNullableDecimal } from '@core/utils/decimals';
import { hasValue } from '@core/utils/equality';

type TruckingFormikFields = Pick<
  ProjectOrQuoteProductFormikType,
  'truckingType' | 'haulRate' | 'minimumHaulCharge' | 'taxRate' | 'haulCost' | 'haulTaxable'
>;

export const createValidateTruckingType =
  (options: LookupInputOption[], errorMessage: string) =>
  (value: unknown): string | undefined => {
    if (!hasValue(value) || value === '') {
      return undefined;
    }
    const id =
      typeof value === 'object' && hasValue(value) && 'id' in value ? (value as any).id : value;
    if (!hasValue(id) || id === '') {
      return undefined;
    }
    const hasMatch = options.some((option) => option.value === id);
    return hasMatch ? undefined : errorMessage;
  };

const toNumberOrNull = (value: string | number | null | undefined): number | null => {
  const cleaned = cleanedNullableDecimal(typeof value === 'number' ? String(value) : value);
  if (cleaned === null) {
    return null;
  }
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
};

const isValueChangedManually = (
  currentValue: string | number | null | undefined,
  defaultValue: string | number | null | undefined,
  selectedTruckingType: TruckingTypeSummary | undefined,
): boolean => {
  if (selectedTruckingType === undefined) {
    return true;
  }
  return toNumberOrNull(currentValue) !== toNumberOrNull(defaultValue);
};

export const useProductTruckTypeSection = ({
  formikBag,
  truckingInfoPathPrefix,
  productPathPrefix,
  truckingTypes,
}: {
  formikBag: FormikContextType<SharedFormikType>;
  truckingInfoPathPrefix: string;
  productPathPrefix: string;
  truckingTypes: List<TruckingTypeSummary>;
}): {
  isHaulRateChangedManually: boolean;
  isMinimumHaulChargeChangedManually: boolean;
  isHaulCostChangedManually: boolean;
  isTruckingTypeOrphaned: boolean;
  availableTruckingTypes: TruckingTypeSummary[];
  selectedTruckingTypeLabel: string | null;
  selectedTruckingType: TruckingTypeSummary | undefined;
} => {
  const formikProduct =
    formikBag.getFieldMeta<ProjectOrQuoteProductFormikType>(productPathPrefix).value;

  const formikTruckingInfo =
    formikBag.getFieldMeta<TruckingFormikFields>(truckingInfoPathPrefix).value;

  const selectedTruckingType: TruckingTypeSummary | undefined = hasValue(
    formikTruckingInfo.truckingType?.id,
  )
    ? truckingTypes?.items.find((tt) => tt.id === formikTruckingInfo.truckingType?.id)
    : undefined;

  const selectedTruckingTypeLabel =
    selectedTruckingType?.name ?? formikTruckingInfo.truckingType?.option?.label ?? null;

  const isHaulRateChangedManually = isValueChangedManually(
    formikTruckingInfo.haulRate?.number ?? null,
    selectedTruckingType?.defaultHaulRate?.number ?? null,
    selectedTruckingType,
  );
  const isMinimumHaulChargeChangedManually = isValueChangedManually(
    formikTruckingInfo.minimumHaulCharge ?? null,
    selectedTruckingType?.minimumHaulCharge ?? null,
    selectedTruckingType,
  );
  const isHaulCostChangedManually = isValueChangedManually(
    formikTruckingInfo.haulCost?.number ?? null,
    selectedTruckingType?.defaultHaulCost?.number ?? null,
    selectedTruckingType,
  );

  const isTruckingTypeOrphaned =
    formikTruckingInfo.truckingType?.id &&
    truckingTypes?.items.find((tt) => tt.id === formikTruckingInfo.truckingType?.id) === undefined;

  const currentTruckingTypeId = formikTruckingInfo.truckingType?.id ?? null;
  const selectedTruckingTypeIds = [
    formikProduct.truckingType?.id ?? null,
    ...(formikProduct.additionalTruckingInfos ?? []).map((info) => info.truckingType?.id ?? null),
  ].filter(hasValue);
  const excludedTruckingTypeIds = new Set(
    selectedTruckingTypeIds.filter((id) => id !== currentTruckingTypeId),
  );
  const availableTruckingTypes = truckingTypes.items.filter(
    (tt) => !excludedTruckingTypeIds.has(tt.id),
  );

  return {
    isHaulRateChangedManually,
    isMinimumHaulChargeChangedManually,
    isHaulCostChangedManually,
    isTruckingTypeOrphaned: Boolean(isTruckingTypeOrphaned),
    availableTruckingTypes,
    selectedTruckingTypeLabel,
    selectedTruckingType,
  };
};
